import { gql, useLazyQuery } from "@apollo/client";
import { useState } from "react";
import CiroDropDown from "./CiroDropdown";
import classNames from "classnames";
import CiroCheckbox from "./CiroCheckbox";
import {
  UserAccountSelector_UserAccountsQuery,
  UserAccountSelector_UserAccountsQueryVariables,
} from "../../__generated__/graphql";

export const UserAccountSelector_UserAccounts = gql`
  query UserAccountSelector_UserAccounts(
    $showDeleted: Boolean
    $searchString: String
  ) {
    userAccounts(showDeleted: $showDeleted, searchString: $searchString) {
      pk
      first
      last
      email
      org {
        id
        name
      }
    }
  }
`;

interface IUserAccountSelectorProps {
  value?: string | null;
  onChange: (userPk: string | null, selectedLabel?: string) => void;
  placeholder?: string;
  label?: string;
  labelClassName?: string;
  showDeletedOption?: boolean;
  error?: string;
}

const UserAccountSelector = ({
  value,
  onChange,
  placeholder = "Select user",
  label,
  labelClassName,
  showDeletedOption = false,
  error,
}: IUserAccountSelectorProps) => {
  const [showDeleted, setShowDeleted] = useState(false);
  const [currentOptions, setCurrentOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);

  const [searchUserAccounts] = useLazyQuery<
    UserAccountSelector_UserAccountsQuery,
    UserAccountSelector_UserAccountsQueryVariables
  >(UserAccountSelector_UserAccounts);

  const formatUserLabel = (user: any) => {
    const fullName = `${user.first || ""} ${user.last || ""}`.trim();
    const displayName = fullName || user.email;
    const orgName = user.org?.name || "Unknown Org";

    if (user.org?.name) {
      return `${displayName} (${orgName})`;
    }
    return displayName;
  };

  const loadOptions = async (inputValue: string, callback: any) => {
    try {
      const { data } = await searchUserAccounts({
        variables: {
          showDeleted,
          searchString: inputValue,
        },
      });
      const newOptions =
        data?.userAccounts?.map((user: any) => ({
          label: formatUserLabel(user),
          value: user.pk,
        })) || [];
      callback(newOptions);
    } catch (error) {
      console.error("Error loading user accounts:", error);
      callback([]);
    }
  };

  return (
    <div>
      <CiroDropDown
        async={true}
        placeholder={placeholder}
        isClearable
        label={label}
        labelClassName={labelClassName}
        value={value}
        options={currentOptions}
        loadOptions={loadOptions}
        defaultOptions={false}
        error={error}
        onChangeFullValues={(option) => {
          onChange(option?.value || null, option?.label);
          if (
            option &&
            !currentOptions.some((opt) => opt.value === option.value)
          ) {
            setCurrentOptions([...currentOptions, option]);
          }
        }}
      />

      {showDeletedOption && (
        <div className={classNames("pt-4")}>
          <CiroCheckbox
            label="Show deleted users"
            checked={showDeleted}
            onClick={() => {
              setShowDeleted(!showDeleted);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default UserAccountSelector;
